import { uploadMeta } from "~/utils/files-cdn"
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

export async function getUnifiedOsFeed(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/unified-os/actions`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function resolveUnifiedOsAction(
  tokenInfo: ITokenInfo,
  actionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/unified-os/actions/${actionId}/resolve`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function generateUnifiedOsActionEmail(
  tokenInfo: ITokenInfo,
  actionId: string,
  payload: {
    to: string
    subject?: string
    body?: string
    stakeholder_name?: string
    stakeholder_company?: string
    attachments?: { file_key: string; filename: string; mime_type?: string }[]
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/unified-os/actions/${actionId}/generate-email`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

// Pure preview draft — no send, no resolve. Returns {subject, body} inside the
// standard {success,data,error} envelope, i.e. res.data.data. Email is the only
// channel; the LinkedIn draft path was removed on both sides.
export async function draftUnifiedOsActionMessage(
  tokenInfo: ITokenInfo,
  actionId: string,
  // "Refine with AI": the rep's instruction plus the draft currently on screen.
  // Sending both turns the call into an edit of that draft; omitting them
  // produces a first draft from the action's evidence, as before.
  refine?: { instruction: string; subject?: string; body?: string }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/unified-os/actions/${actionId}/draft-message`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      channel: "email",
      ...(refine
        ? {
            refine_instruction: refine.instruction,
            current_subject: refine.subject ?? "",
            current_body: refine.body ?? ""
          }
        : {})
    }
  })
}

// POST presign: get an S3 PUT url + file_key for a single email attachment
// scoped to this action.
export async function presignUnifiedOsActionAttachment(
  tokenInfo: ITokenInfo,
  actionId: string,
  filename: string,
  mimeType: string,
  size?: number
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/unified-os/actions/${actionId}/attachments/presign`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: { filename, mime_type: mimeType, size }
  })
}

// PUT the file straight to S3 with the presigned url (no auth header — the
// signature is in the url; an Authorization header would break it).
export async function uploadUnifiedOsAttachmentFile(
  uploadUrl: string,
  file: File
) {
  const resp = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": uploadMeta(file).type }
  })
  if (!resp.ok) throw new Error(`S3 upload failed: ${resp.status}`)
}

export async function undoUnifiedOsAction(
  tokenInfo: ITokenInfo,
  actionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/unified-os/actions/${actionId}/undo`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// Files a resolved action away immediately instead of leaving it struck
// through on the feed for the rest of its week.
export async function archiveUnifiedOsAction(
  tokenInfo: ITokenInfo,
  actionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/unified-os/actions/${actionId}/archive`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// Actions archived in the last week. A separate endpoint from the feed rather
// than a query param on it — reading the archive must not trigger card
// generation or advance the "New" badge watermark that getFeed owns.
export async function getUnifiedOsArchive(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/unified-os/actions/archive`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}
